import type {
  AssistantActionPlan,
  AssistantPlanCandidate,
  AssistantPlanReference,
  AssistantResolvedReference,
} from '@/lib/api/assistant.api'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { assistantApi } from '@/lib/api/assistant.api'
import { cn } from '@/lib/utils'
import { Check } from '@phosphor-icons/react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { describeArgs } from './confirm-fields'

export const actionPlanQueryKey = (planId: string) => ['action-plan', planId]

const MODULE_NOUNS: Record<string, string> = {
  TASK: 'task',
  TICKET: 'ticket',
  PROJECT: 'project',
  DEAL: 'deal',
  CONTACT: 'contact',
  ENTITY: 'client',
  USER: 'person',
}

function candidateLabel(candidate: AssistantPlanCandidate): {
  primary: string
  secondary: string | null
} {
  if (candidate.module === 'USER') {
    return {
      primary: candidate.name ?? candidate.email ?? 'Unnamed',
      secondary: candidate.name ? (candidate.email ?? null) : null,
    }
  }
  return {
    primary: candidate.name ?? candidate.identifier ?? 'Unnamed',
    secondary: candidate.name ? (candidate.identifier ?? null) : null,
  }
}

/**
 * The action-plan card (Benny UX pass 3, P3-7). Rendered as a thread message
 * from `metadata.plan` on both surfaces and hydrated from the plan endpoint,
 * so it survives a reload. Ambiguous names render as a picker; choosing a
 * candidate re-plans with that answer instead of sending it to chat (07 §D14).
 * Personal accounts: adapted, same card (business actions are already hidden
 * from the personal catalog).
 */
export function AssistantPlanCard({
  planId,
  initialStatus,
  onReplan,
  onEdit,
  className,
}: {
  planId: string
  /** The status stamped on the message, shown until the plan loads. */
  initialStatus?: string
  /** A picker choice: rebuild the plan with the answers so far. */
  onReplan?: (
    instruction: string,
    resolvedReferences: AssistantResolvedReference[],
  ) => void
  /** Put the instruction back in the composer to change it. */
  onEdit?: (instruction: string) => void
  className?: string
}) {
  const queryClient = useQueryClient()
  const planQuery = useQuery<AssistantActionPlan>({
    queryKey: actionPlanQueryKey(planId),
    queryFn: () => assistantApi.getActionPlan(planId),
    staleTime: 30_000,
  })
  const [executing, setExecuting] = useState(false)
  const [replanning, setReplanning] = useState<string | null>(null)
  const plan = planQuery.data
  // Once a newer card exists this one can no longer be answered, so its
  // "rebuilding" note goes with the ability to pick.
  useEffect(() => {
    if (!onReplan) setReplanning(null)
  }, [onReplan])

  if (!plan) {
    return (
      <section
        className={cn(
          'space-y-2 rounded-2xl border border-border/50 bg-background p-4 shadow-sm',
          className,
        )}
      >
        <div className="text-xs font-semibold text-foreground/70">
          {planQuery.isError
            ? 'This plan is no longer available.'
            : initialStatus === 'completed'
              ? 'Plan completed'
              : 'Loading the plan…'}
        </div>
        {!planQuery.isError && (
          <>
            <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
            <div className="h-3 w-3/5 animate-pulse rounded bg-muted/70" />
          </>
        )}
      </section>
    )
  }

  const finished = plan.status === 'completed'
  const failed = plan.status === 'failed'
  const expired = new Date(plan.expiresAt).getTime() < Date.now()
  const ambiguous = (plan.references ?? []).filter(
    reference =>
      reference.status === 'ambiguous' && reference.candidates.length > 0,
  )
  // A question the picker already asks is not repeated below it.
  const openQuestions = plan.questions.filter(
    question =>
      !ambiguous.some(reference => question.includes(`"${reference.value}"`)),
  )
  // Names for the ids the steps carry: every candidate the server matched.
  const resolvedNames: Record<string, string> = {}
  for (const reference of plan.references ?? []) {
    for (const candidate of reference.candidates) {
      const label = candidateLabel(candidate)
      // People read as their name; records as "ASP-45 Test new 3".
      resolvedNames[candidate.id] =
        candidate.module !== 'USER' && label.secondary
          ? `${label.secondary} ${label.primary}`
          : label.primary
    }
  }
  const resolvedSoFar: AssistantResolvedReference[] = (plan.references ?? [])
    .filter(
      reference =>
        reference.status === 'resolved' && reference.candidates.length === 1,
    )
    .map(reference => ({
      module: reference.module,
      value: reference.value,
      id: reference.candidates[0]!.id,
    }))

  const execute = async () => {
    if (!plan.executable || executing) return
    setExecuting(true)
    try {
      const result = await assistantApi.executeActionPlan(plan.planId)
      queryClient.setQueryData<AssistantActionPlan>(
        actionPlanQueryKey(planId),
        current => (current ? { ...current, status: result.status } : current),
      )
      if (result.status === 'completed') {
        toast.success('Benny completed the approved plan.')
      } else {
        toast.error('Benny stopped when a plan step failed.')
      }
    } catch (error: any) {
      toast.error(error?.message || 'The action plan could not be executed.')
    } finally {
      setExecuting(false)
    }
  }

  const pick = (
    reference: AssistantPlanReference,
    candidate: AssistantPlanCandidate,
  ) => {
    if (!onReplan || replanning) return
    setReplanning(reference.value)
    onReplan(plan.instruction, [
      ...resolvedSoFar,
      { module: reference.module, value: reference.value, id: candidate.id },
    ])
  }

  return (
    <section
      className={cn(
        'overflow-hidden rounded-2xl border border-border/55 bg-background shadow-sm',
        className,
      )}
      data-testid="assistant-plan-card"
    >
      <div className="border-b border-border/40 px-4 py-3.5">
        <div className="mb-1 flex items-center gap-2">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              finished
                ? 'bg-emerald-500'
                : failed
                  ? 'bg-red-500'
                  : 'bg-sky-500',
            )}
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            {finished
              ? 'Completed'
              : failed
                ? 'Stopped'
                : expired
                  ? 'Expired'
                  : 'Review before running'}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-foreground">{plan.title}</h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {plan.summary}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground/80">
          You asked: {plan.instruction}
        </p>
      </div>

      {plan.steps.length > 0 && (
        <ol className="divide-y divide-border/35 px-4">
          {plan.steps.map((step, index) => {
            const fields = describeArgs(step.args ?? {}, resolvedNames)
            return (
              <li key={step.id} className="flex gap-3 py-3">
                <span
                  className={cn(
                    'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
                    step.permission === 'blocked'
                      ? 'bg-red-500/10 text-red-600'
                      : 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
                  )}
                >
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-xs font-medium leading-relaxed text-foreground/90">
                      {step.description}
                    </p>
                    <span className="shrink-0 rounded-full bg-muted/70 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {step.risk} risk
                    </span>
                  </div>
                  {step.blockedReason ? (
                    <p className="mt-0.5 text-[11px] leading-relaxed text-red-600 dark:text-red-400">
                      {step.blockedReason}
                    </p>
                  ) : null}
                  {fields.length > 0 && (
                    <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                      {fields.map(field => (
                        <div key={field.label + field.value} className="contents">
                          <dt className="text-muted-foreground">{field.label}</dt>
                          <dd className="min-w-0 truncate text-foreground/90">
                            {field.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}

      {ambiguous.length > 0 && !finished && !failed && (
        <div className="mx-4 my-3 space-y-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
          {ambiguous.map(reference => (
            <div key={`${reference.module}:${reference.value}`}>
              <p className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
                Which {MODULE_NOUNS[reference.module] ?? 'record'} did you mean
                by &ldquo;{reference.value}&rdquo;?
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {reference.candidates.map(candidate => {
                  const label = candidateLabel(candidate)
                  return (
                    <button
                      key={candidate.id}
                      type="button"
                      disabled={!onReplan || replanning !== null}
                      onClick={() => pick(reference, candidate)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-background px-2.5 py-1 text-xs text-foreground transition-colors hover:border-amber-600 hover:bg-amber-500/10 disabled:opacity-60"
                    >
                      <span className="font-medium">{label.primary}</span>
                      {label.secondary && (
                        <span className="text-muted-foreground">
                          {label.secondary}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
          {replanning && (
            <p className="text-[11px] text-amber-900/80 dark:text-amber-100/80">
              Rebuilding the plan with that answer…
            </p>
          )}
        </div>
      )}

      {openQuestions.length > 0 && (
        <div className="mx-4 mb-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-3 py-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
            Benny needs your input
          </p>
          {openQuestions.map(question => (
              <p
                key={question}
                className="mt-1 text-xs text-amber-900/80 dark:text-amber-100/80"
              >
                {question}
              </p>
            ))}
        </div>
      )}

      <div className="flex flex-col items-stretch gap-2 border-t border-border/40 bg-muted/20 px-4 py-3">
        <p className="min-w-0 flex-1 text-[10px] leading-relaxed text-muted-foreground">
          Nothing runs until you approve. Permissions are checked again at
          execution.
        </p>
        <div className="flex shrink-0 items-center justify-end gap-2">
          {!finished && !failed && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(plan.instruction)}
              disabled={executing}
              className="rounded-full px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              Edit request
            </button>
          )}
          <Button
            size="sm"
            onClick={() => void execute()}
            disabled={
              !plan.executable || executing || finished || failed || expired
            }
            className={cn(
              'h-8 rounded-full px-3.5 text-xs shadow-sm',
              finished && 'bg-emerald-600 hover:bg-emerald-600',
            )}
          >
            {executing ? (
              <>
                <span className="benny-tool-spinner mr-1.5" /> Running…
              </>
            ) : finished ? (
              <>
                <Check className="mr-1.5 h-3.5 w-3.5" /> Completed
              </>
            ) : failed ? (
              'Stopped'
            ) : (
              'Approve & run'
            )}
          </Button>
        </div>
      </div>
    </section>
  )
}
